import type { NewsquawkCategory } from "@/pages/PortfolioBuilder/newsquawkCategories";
import type { AssetClassId } from "./types";

/** Options shown as multi-select cards in the welcome flow's profile step. */
export const ASSET_CLASS_OPTIONS: {
  id: AssetClassId;
  label: string;
  blurb: string;
}[] = [
  { id: "equities", label: "Equities", blurb: "Single names, indices & earnings" },
  { id: "fx", label: "FX", blurb: "Currencies & central banks" },
  { id: "fixed_income", label: "Fixed Income", blurb: "Rates, bonds & yields" },
  { id: "energy", label: "Energy", blurb: "Oil, gas & power" },
  { id: "metals", label: "Metals", blurb: "Gold, silver & base metals" },
  { id: "crypto", label: "Crypto", blurb: "Digital assets" },
  { id: "macro", label: "Macro", blurb: "Data, geopolitics & the big picture" },
];

/**
 * Squawk channel roles are the best existing signal for what a trader cares
 * about — used to pre-check asset classes in the profile step.
 */
const ROLE_TO_ASSET_CLASS: Record<string, AssetClassId[]> = {
  "squawk-equities": ["equities"],
  "squawk-forex": ["fx"],
  "squawk-fixed-income": ["fixed_income"],
  "squawk-energy": ["energy"],
  "squawk-metals": ["metals"],
  "squawk-multi-asset": ["macro"],
};

export function assetClassesFromRoles(roles: string[]): AssetClassId[] {
  const result = new Set<AssetClassId>();
  for (const role of roles) {
    for (const ac of ROLE_TO_ASSET_CLASS[role] ?? []) {
      result.add(ac);
    }
  }
  return [...result];
}

/**
 * Which prebuilt-watchlist library categories are most relevant per asset
 * class — used to sort/expand categories in the watchlist step. Users can
 * still browse the full library, so this only shapes ordering.
 */
export const ASSET_CLASS_TO_CATEGORIES: Record<
  AssetClassId,
  NewsquawkCategory[]
> = {
  equities: [
    "Indices",
    "Technology",
    "Financials",
    "Consumer",
    "Healthcare",
    "Industrials",
    "Communications",
  ],
  fx: ["Macro"],
  fixed_income: ["Macro", "Financials"],
  energy: ["Energy", "Utilities"],
  metals: ["Materials", "Macro"],
  crypto: ["Technology", "Other"],
  macro: ["Macro", "Indices"],
};

/** Ordered, de-duplicated relevant categories for a set of answers. */
export function relevantCategories(
  assetClasses: AssetClassId[]
): NewsquawkCategory[] {
  const seen = new Set<NewsquawkCategory>();
  const ordered: NewsquawkCategory[] = [];
  for (const ac of assetClasses) {
    for (const cat of ASSET_CLASS_TO_CATEGORIES[ac] ?? []) {
      if (!seen.has(cat)) {
        seen.add(cat);
        ordered.push(cat);
      }
    }
  }
  return ordered;
}
